import { ModulesCtx, PreloadedModule, RuntimeAPI } from "@dynamic/types";
import React, { useEffect, useMemo, useState, useContext } from "react";
import {
  PreloadedModulesCtx,
  RegisterSSRModuleCtx,
  viteReactLoadableApiKey,
} from "./shared";

declare global {
  interface Window {
    [viteReactLoadableApiKey]: RuntimeAPI;
  }
}

type Generatedoptions = {
  syncLoader: () => (...args: any) => JSX.Element;
  module: string;
  prefetcher: () => Promise<PreloadedModule>;
};

const moduleProxyMap: Map<PreloadedModule, PreloadedModule> = new Map();

export function importSync(path: string) {
  const preloadedModules = useContext(PreloadedModulesCtx);
  if (preloadedModules[path]) {
    const mod = preloadedModules[path];
    if (!moduleProxyMap.has(mod)) {
      moduleProxyMap.set(
        mod,
        new Proxy(mod, {
          get(target, prop, reciever) {
            if (prop === "then") {
              return function then(cb: (mod: any) => void) {
                return cb(mod);
              };
            }
            return Reflect.get(target, prop, reciever);
          },
        })
      );
    }
    return moduleProxyMap.get(mod);
  }
}

const nullComp = () => {
  // cheeky workaround different hook pattern, hopefully doesn't break fast refresh
  const preloadedModules = useContext(PreloadedModulesCtx);
  return () => null;
};

interface DynamicOptions {
  ssr?: boolean;
  /** used by build system */
  generatedOptions?: Generatedoptions;
}

interface DynamicComponentHookData<
  Component extends React.ComponentType<Props>,
  Props extends any
> {
  hookData: {
    componentPromise: () => Promise<Component>;
    options: DynamicOptions;
  };
}

let globalApi: RuntimeAPI | undefined;

if (typeof window !== "undefined") {
  globalApi = window[viteReactLoadableApiKey];
} else {
  globalApi = global[viteReactLoadableApiKey];
}

export function createNewModulesPromise(api: RuntimeAPI) {
  return function () {
    const loadedModules: ModulesCtx = {};
    const promises = Object.entries(api.modulePromises).map(([path, module]) =>
      module.then((result) => {
        loadedModules[path] = result;
      })
    );
    return Promise.all(promises).then(() => loadedModules);
  };
}

export default function dynamic<
  Component extends React.ComponentType<Props>,
  Props extends any
>(componentPromise: () => Promise<Component>, options: DynamicOptions = {}) {
  const hookData: DynamicComponentHookData<Component, Props> = {
    hookData: {
      componentPromise,
      options,
    },
  };

  // code for server side to ensure all modules are preloaded before ssr render
  if (
    typeof window === "undefined" &&
    globalApi &&
    options.ssr !== false &&
    options.generatedOptions?.prefetcher &&
    !globalApi.modulePromises[options.generatedOptions.module] &&
    globalApi.importChunkMap[options.generatedOptions.module]
  ) {
    // start loading module as soon as its found
    globalApi.modulePromises[
      options.generatedOptions.module
    ] = options.generatedOptions?.prefetcher();

    console.log("preload", options.generatedOptions.module);

    // ensure continue to wait for new modules
    if (!globalApi?.newModules)
      globalApi.newModules = createNewModulesPromise(globalApi);
  }

  function DynamicComponentWrapper(props: any) {
    const [Component] = useDynamicComponent(hookData);
    return useMemo(() => {
      return <Component {...props}></Component>;
    }, [props, Component]);
  }

  Object.assign(DynamicComponentWrapper, {
    ...hookData,
    waitFor() {
      options;
    },
  });

  return DynamicComponentWrapper as Component &
    DynamicComponentHookData<Component, Props>;
}

export function useDynamicComponent<
  Component extends React.ComponentType<Props>,
  Props extends any
>(dynamicImport?: DynamicComponentHookData<Component, Props>) {
  const { componentPromise, options } = dynamicImport?.hookData || {};
  const ssrContext = useContext(RegisterSSRModuleCtx);
  const preloadedModules = useContext(PreloadedModulesCtx);
  const canLoadSync =
    options?.generatedOptions &&
    options.ssr !== false &&
    preloadedModules[options.generatedOptions.module]
      ? true
      : false || false;

  let initialState: () => React.ComponentType<any> = useMemo(() => {
    if (canLoadSync && options?.generatedOptions) {
      return options.generatedOptions.syncLoader;
    } else {
      return nullComp;
    }
  }, [dynamicImport]);

  if (
    ssrContext &&
    typeof window === "undefined" &&
    options?.generatedOptions?.module
  ) {
    ssrContext.add(options.generatedOptions.module);
  }

  const init = initialState();

  const [Component, setComponent] = useState(() => init);
  const [isLoading, setIsLoading] = useState(!canLoadSync);

  useEffect(() => {
    let cancelled = false;
    if (!canLoadSync) {
      // if the componentPromise changes, we change loading state
      if (!isLoading) setIsLoading(() => true);

      if (componentPromise) {
        componentPromise().then((component) => {
          if (cancelled) return;
          setComponent(() => component);
          setIsLoading(() => false);
        });

        waitFor();
      }
    } else {
      if (options?.generatedOptions && options.generatedOptions.syncLoader) {
        if (Component !== init) {
          setComponent(() => init);
          setIsLoading(false);
        }
      }
    }

    return () => {
      cancelled = true;
    };
  }, [componentPromise]);

  function waitFor() {
    if (
      typeof window !== "undefined" &&
      options?.generatedOptions &&
      preloadedModules
    ) {
      const path = options.generatedOptions?.module;
      options.generatedOptions.prefetcher().then((module: PreloadedModule) => {
        preloadedModules[path] = module;
        return module;
      });
    }
  }

  return [Component, isLoading] as const;
}
